/**
 * Meta Pixel Purchase-event tracker.
 *
 * Fires a `Purchase` event (Meta's standard e-commerce conversion)
 * only AFTER the payment has been confirmed server-side, using
 * sessionStorage to guard against a double-fire on refresh.
 *
 * Architecture mirrors `googleAds.js` + `analytics.js` (GA4) exactly —
 * one shared server-confirmed trigger point in BookingSuccessPage.jsx
 * fires all three trackers, each with its own sessionStorage guard so
 * a duplicate-fire on one channel can't corrupt the others.
 *
 * Consent gating:
 *   * The Pixel is loaded on every pageload from public/index.html but
 *     is placed in `fbq('consent','revoke')` state before init. In that
 *     state, calling `fbq('track', 'Purchase', ...)` does NOT dispatch
 *     to Meta — the call is queued locally and only replayed if consent
 *     is later granted. Which means: this function is safe to call
 *     unconditionally on a confirmed payment. If the visitor rejected
 *     cookies, Meta simply never receives the event.
 *   * ConsentBanner.jsx flips grant/revoke on Accept/Reject.
 *
 * Notes on choice of "Purchase":
 *   Meta's standard events (`Purchase`, `Lead`, `CompleteRegistration`,
 *   etc.) are what the Ads Manager uses for pre-built optimisation
 *   goals + Advantage+ campaigns. A custom event would work for
 *   reporting but not for bid optimisation. Since the deposit IS a real
 *   monetary conversion (customer paid), `Purchase` is the correct
 *   choice + matches how Google Ads treats the same signal.
 */

// Pixel ID mirrors the value hardcoded in public/index.html — kept as
// a constant here so the helper doesn't have to reach into globals.
// If the ID ever changes, update both the pixel script tag AND this
// constant together.
const PIXEL_ID = "1031124436624972";

/**
 * Verifies the Pixel is fully-loaded, not just the stub queue-pusher.
 * The base snippet in index.html defines `window.fbq` as a function
 * IMMEDIATELY, but that function is a queue-pusher stub until
 * fbevents.js loads and hooks `fbq.callMethod`. Calls made against
 * the stub go into `fbq.queue`, and after the async fbevents.js
 * loads it drains that queue. HOWEVER — under Meta's consent-revoke
 * behaviour (Feb 2026), events tracked before consent is granted
 * are DROPPED, not replayed. So relying on the stub queue for
 * consent-gated events is unsafe.
 *
 * `callMethod` becomes defined only once fbevents.js has finished
 * loading + attaching handlers, which is the earliest safe moment
 * to fire consent-gated track events.
 */
const _fbqReady = () =>
  typeof window !== "undefined" &&
  typeof window.fbq === "function" &&
  typeof window.fbq.callMethod === "function";

/**
 * Dispatch a Purchase event, retrying with backoff if fbevents.js is
 * still loading. Never throws — a tracking outage must not break the
 * booking confirmation UI. Returns a Promise that resolves to true
 * once the underlying fbq call executed cleanly, or false if the
 * pixel never became ready within the retry budget.
 */
const _dispatchWithRetry = (payload, opts, transaction_id, maxAttempts = 20) => {
  return new Promise((resolve) => {
    let attempts = 0;
    const tick = () => {
      attempts += 1;
      if (_fbqReady()) {
        try {
          window.fbq("track", "Purchase", payload, opts);
          // callMethod invocation is synchronous; if we're here without
          // an exception, the pixel has accepted the event and its
          // internal transport layer will dispatch the beacon to
          // facebook.com/tr — no per-request confirmation is exposed
          // by Meta's API, so this is as far as client-side visibility
          // goes. Any Meta-side drop (consent revoked internally,
          // browser tracking-prevention, etc.) is invisible here.
          console.info(
            `[meta] Purchase dispatched: value=${payload.value} ${payload.currency} ` +
            `txn=${transaction_id} eventID=${opts.eventID} attempt=${attempts}`
          );
          resolve(true);
        } catch (err) {
          console.error(
            `[meta] fbq('track','Purchase') threw on attempt ${attempts}:`,
            err
          );
          resolve(false);
        }
        return;
      }
      if (attempts >= maxAttempts) {
        console.error(
          `[meta] fbq.callMethod never ready after ${attempts} attempts — ` +
          "Purchase NOT dispatched. fbevents.js may be blocked by the network " +
          "or an extension the app cannot see."
        );
        resolve(false);
        return;
      }
      // 250ms × 20 = 5s total budget. Long enough for a slow 3G load
      // of fbevents.js from cache-miss, short enough that a real
      // network block surfaces in the console within the payment
      // confirmation UI's own poll window.
      setTimeout(tick, 250);
    };
    tick();
  });
};

export const fireMetaPixelPurchase = ({ transaction_id, value, currency = "EUR" }) => {
  if (typeof window === "undefined") return false;
  if (!transaction_id) {
    console.warn("[meta] missing transaction_id — Purchase not fired");
    return false;
  }

  // Session-scoped double-fire guard. Same shape as the Ads/GA4 guards
  // in googleAds.js / analytics.js so all three channels dedupe on the
  // same booking_id.
  const guardKey = `meta_purchase_${transaction_id}`;
  try {
    if (sessionStorage.getItem(guardKey)) return false;
    sessionStorage.setItem(guardKey, "1");
  } catch (e) {
    // sessionStorage disabled — best-effort fire, small risk of a
    // duplicate on refresh but Meta dedupes server-side using eventID.
  }

  const payload = {
    value: Number(value) || 0,
    currency,
  };
  const opts = {
    // `eventID` enables Meta's Conversions API + Pixel deduplication.
    // Even without a CAPI server-side counterpart today, sending this
    // makes any future CAPI integration a drop-in — the two events
    // will merge automatically on shared eventID.
    eventID: `booking_${transaction_id}`,
  };

  // Honest logging: "attempted" happens now, "dispatched" happens
  // inside _dispatchWithRetry ONLY if fbq.callMethod actually ran.
  // Previously this file logged "fired Purchase" unconditionally BEFORE
  // the fbq call, which is what hid the production silent-drop bug.
  console.info(
    `[meta] Purchase attempted: value=${value} ${currency} txn=${transaction_id}`
  );

  // Return-shape stability: we return true synchronously (matching the
  // old contract used by BookingSuccessPage) as long as the pre-flight
  // gates all pass. The retry-then-dispatch happens asynchronously and
  // reports its final outcome via console. The sessionStorage guard
  // is already set at this point so a synchronous double-call still
  // dedupes cleanly.
  _dispatchWithRetry(payload, opts, transaction_id);
  return true;
};

// Exposed for tests / dev tools — read-only reference.
export const META_PIXEL_ID = PIXEL_ID;
